import { motion } from 'framer-motion';
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Mail, Phone, Clock } from 'lucide-react';
import Footer from '@/components/pages/shared/Footer';
import HeadSEO from '@/components/seo/HeadSEO';

const ContactUs = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const contacts = [
    {
      icon: Mail,
      key: 'email',
      value: 'info@bookoll.com',
    },
    {
      icon: Phone,
      key: 'phone',
      value: '+34 662 01 25 73',
    },
    {
      icon: Clock,
      key: 'schedule',
      value: `${t('contactUs.scheduleText')} · 18:00 – 21:00`,
    },
  ];

  return (
    <div className="flex flex-col min-h-screen bg-white dark:bg-black text-gray-800 dark:text-gray-200">
      {/* ── SEO ───────────────────────────────────────────────────────────── */}
      <HeadSEO
        title="Contacto — Bookoll"
        description="Contacta con el equipo de Bookoll. Estamos disponibles por email, teléfono o en nuestro horario de atención para resolver cualquier duda."
        canonical="https://bookoll.com/contact"
      />

      <main className="flex-grow">
        {/* ── Hero ──────────────────────────────────────────────────────── */}
        <section className="relative overflow-hidden text-white w-full">
          {/* Fondo modo claro */}
          <div
            className="absolute inset-0 dark:hidden"
            style={{
              background: `
                radial-gradient(ellipse at 15% 60%, rgba(40,142,127,0.22) 0%, transparent 50%),
                radial-gradient(ellipse at 85% 20%, rgba(16,50,84,0.5) 0%, transparent 55%),
                linear-gradient(170deg, #103254 0%, #103254 25%, #1a4a6e 45%, #3d7fa0 68%, #a8cfe0 84%, #e8f4f8 95%, #ffffff 100%)
              `,
            }}
          />
          {/* Fondo modo oscuro: negro con resplandor teal, igual que Login */}
          <div
            className="absolute inset-0 hidden dark:block"
            style={{
              background: `
                radial-gradient(ellipse at 15% 55%, rgba(40, 142, 127, 0.35) 0%, transparent 55%),
                radial-gradient(ellipse at 85% 15%, rgba(16, 50, 84, 0.45) 0%, transparent 55%),
                linear-gradient(170deg, #050807 0%, #0a1a16 22%, #123a30 42%, #103254 60%, #0a0f14 82%, #000000 100%)
              `,
            }}
          />

          <motion.div
            className="absolute top-10 left-[5%] w-56 h-56 rounded-full pointer-events-none"
            style={{
              background:
                'radial-gradient(circle, rgba(40,142,127,0.18) 0%, transparent 70%)',
              filter: 'blur(40px)',
            }}
            animate={{ scale: [1, 1.1, 1], opacity: [0.5, 0.9, 0.5] }}
            transition={{ duration: 7, repeat: Infinity, ease: 'easeInOut' }}
          />
          <motion.div
            className="absolute top-4 right-[8%] w-64 h-64 rounded-full pointer-events-none"
            style={{
              background:
                'radial-gradient(circle, rgba(255,255,255,0.07) 0%, transparent 70%)',
              filter: 'blur(50px)',
            }}
            animate={{ scale: [1, 1.08, 1], opacity: [0.4, 0.75, 0.4] }}
            transition={{
              duration: 9,
              repeat: Infinity,
              ease: 'easeInOut',
              delay: 3,
            }}
          />

          <button
            onClick={() => navigate(-1)}
            aria-label="Volver atrás"
            className="absolute top-5 left-5 z-20 flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full px-4 py-2 text-sm text-white hover:bg-white/20 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            <span className="hidden sm:inline font-medium">Volver</span>
          </button>

          <div className="relative z-10 pt-20 pb-28 px-6 flex flex-col items-center text-center max-w-3xl mx-auto">
            <motion.h1
              className="text-4xl md:text-5xl font-bold tracking-tight"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, ease: 'easeOut', delay: 0.1 }}
            >
              {t('contactUs.title')}
            </motion.h1>

            <motion.div
              className="mt-4 h-px w-16 mx-auto"
              style={{
                background:
                  'linear-gradient(90deg, transparent, rgba(255,255,255,0.6), transparent)',
              }}
              initial={{ scaleX: 0 }}
              animate={{ scaleX: 1 }}
              transition={{ duration: 0.7, delay: 0.5 }}
            />

            <motion.p
              className="mt-4 text-base text-white/70 max-w-xl leading-relaxed"
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, ease: 'easeOut', delay: 0.3 }}
            >
              {t('contactUs.subtitle')}
            </motion.p>
          </div>

          <div className="absolute bottom-0 left-0 w-full overflow-hidden leading-none pointer-events-none">
            <svg
              viewBox="0 0 1440 60"
              preserveAspectRatio="none"
              className="w-full h-10 sm:h-14"
            >
              <path
                d="M0,30 C360,60 1080,0 1440,30 L1440,60 L0,60 Z"
                className="fill-white dark:fill-black"
              />
            </svg>
          </div>
        </section>

        {/* ── Cards de contacto ─────────────────────────────────────────── */}
        <section className="py-20 px-6 max-w-4xl mx-auto w-full bg-white dark:bg-black">
          <div className="grid gap-6 md:grid-cols-3">
            {contacts.map(({ icon: Icon, key, value }, i) => (
              <motion.div
                key={key}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                whileHover={{ y: -4 }}
                className="group flex flex-col items-center text-center p-8 rounded-xl border border-gray-200 dark:border-white/10 bg-white dark:bg-[#1c1c1e] shadow-sm hover:shadow-md hover:border-gray-300 dark:hover:border-white/20 transition-all duration-300"
              >
                <div className="w-12 h-12 rounded-xl bg-[#103254]/6 dark:bg-white/10 flex items-center justify-center mb-5 group-hover:bg-[#103254]/10 dark:group-hover:bg-white/15 transition-colors">
                  <Icon className="w-5 h-5 text-[#103254] dark:text-white" />
                </div>
                <p className="text-sm font-semibold text-gray-700 dark:text-gray-200 mb-1.5">
                  {t(`contactUs.${key}`)}
                </p>
                <p className="text-sm text-gray-500 dark:text-gray-400 leading-relaxed">{value}</p>
              </motion.div>
            ))}
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default ContactUs;